import React from "react";
import blueSkyImage from "../../../assets/images/email_logos/bluesky.png";
import instagramImage from "../../../assets/images/email_logos/instagram.png";
import linkedInImage from "../../../assets/images/email_logos/linked_in.png";
import threadsImage from "../../../assets/images/email_logos/threads.png";
import xTwitterImage from "../../../assets/images/email_logos/x_twitter.png";
import facebookImage from "../../../assets/images/email_logos/facebook.png";
import qwotedLogoImage from "../../../assets/images/qwoted-logo.png";

/**
 * EmailSignature Component
 *
 * Displays an email signature that closely matches the Rails ERB template used in actual emails.
 * Based on components/notify/app/views/notify/layouts/email/_signature.html.erb
 *
 * @param {string} name - Full name
 * @param {string} title - Job title
 * @param {string} email - Email address
 * @param {string|React.ReactNode} image - Image filename/path or React component for the photo
 * @param {string} bookCallLink - Optional link for booking a call (e.g., Hubspot meeting link)
 * @param {string} phoneNumber - Optional phone number (e.g., "+1 (917) 983-9221")
 *
 * Example usage:
 * <EmailSignature
 *   name="Shelby Bridges"
 *   title="Director of User Success"
 *   email="shelby@qwoted.com"
 *   image="shelby.png"
 *   bookCallLink="https://app.hubspot.com/meetings/shelby"
 *   phoneNumber="+1 (917) 983-9221"
 * />
 */
export default function EmailSignature({ name, title, email, image = null, bookCallLink = null, phoneNumber = null }) {
  const extraLineDisplayed = bookCallLink || phoneNumber;
  const emailUtmParams = "utm_source=signature&utm_medium=email";
  const marketingSiteUrl = "https://qwoted.com"; // Replace with actual AppConf value if needed
  const linkedinUrl = "https://www.linkedin.com/company/qwoted";
  const twitterUrl = "https://twitter.com/qwoted";
  const blueskyUrl = "https://bsky.app/profile/qwoted.com";
  const instagramUrl = "https://www.instagram.com/qwoted";
  const threadsUrl = "https://www.threads.net/@qwoted";
  const facebookUrl = "https://www.facebook.com/qwoted";

  return (
    <>
      <br />

      <table border="0" cellSpacing="0" cellPadding="0" style={{ borderCollapse: "collapse" }}>
        <tbody>
          <tr>
            {image && (
              <>
                <td valign="top">
                  <p style={{ lineHeight: "1.2", marginTop: "0", marginBottom: "0" }}>
                    {typeof image === "string" ? (
                      <img src={image} width="100" height="100" alt={`${name}, Qwoted`} />
                    ) : (
                      image
                    )}
                  </p>
                  <p style={{ marginTop: "10px", marginBottom: "0" }}>
                    <a href={`${marketingSiteUrl}?${emailUtmParams}`} target="_blank" rel="noopener noreferrer">
                      <img src={qwotedLogoImage} width="96" alt="Qwoted logo" />
                    </a>
                  </p>
                </td>
                <td width="15" />
              </>
            )}
            <td valign="top">
              <p style={{ lineHeight: "1.68", marginTop: "0", marginBottom: "0" }}>
                <b>
                  <span style={{ fontSize: "17.33px" }}>{name}</span>
                </b>
              </p>
              <p style={{ lineHeight: "1.68", marginTop: "0", marginBottom: "0" }}>
                <span style={{ fontSize: "13.33px" }}>
                  {title},{" "}
                  <b>
                    Qwoted
                    <br />
                  </b>
                </span>
                <span style={{ fontSize: "13.33px" }}>
                  <a
                    href={`mailto:${email}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "underline", color: "rgb(17,85,204)" }}
                  >
                    {email}
                  </a>
                </span>
                <span style={{ color: "rgb(17,85,204)" }}>{" | "}</span>
                <span>
                  <a
                    href={`${marketingSiteUrl}?${emailUtmParams}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "underline", color: "rgb(17,85,204)" }}
                  >
                    qwoted.com
                  </a>
                </span>
                <span>
                  <u />
                  <u />
                </span>
              </p>

              {(bookCallLink || phoneNumber) && (
                <p style={{ lineHeight: "2.3", marginTop: "0", marginBottom: "0", fontSize: "13.33px" }}>
                  {bookCallLink && (
                    <span>
                      <a
                        href={bookCallLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: "underline", color: "rgb(17,85,204)" }}
                      >
                        Book a call
                      </a>
                    </span>
                  )}
                  {bookCallLink && phoneNumber && <span style={{ color: "rgb(17,85,204)" }}>{" | "}</span>}
                  {phoneNumber && (
                    <span>
                      <img
                        src="https://fonts.gstatic.com/s/e/notoemoji/16.0/1f4de/72.png"
                        width="16"
                        alt="📞"
                        loading="lazy"
                        style={{ verticalAlign: "text-bottom" }}
                      />
                      {phoneNumber}
                    </span>
                  )}
                </p>
              )}

              {!image && (
                <p style={{ marginTop: "50px", marginBottom: "0", marginRight: "15px" }}>
                  <a href={`${marketingSiteUrl}?${emailUtmParams}`} target="_blank" rel="noopener noreferrer">
                    <img src={qwotedLogoImage} width="128" alt="Qwoted logo" />
                  </a>
                </p>
              )}

              <p
                style={{
                  lineHeight: "1.5",
                  marginTop: extraLineDisplayed || !image ? "8px" : "27px",
                  marginBottom: "0",
                }}
              >
                <span>
                  <a
                    href={`${linkedinUrl}?${emailUtmParams}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <img src={linkedInImage} width="20" alt="LinkedIn" />
                  </a>
                </span>
                <span>&nbsp; </span>
                <span>
                  <a
                    href={`${twitterUrl}?${emailUtmParams}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <img src={xTwitterImage} width="20" alt="X/Twitter" />
                  </a>
                </span>
                <span>&nbsp; </span>
                <span>
                  <a
                    href={`${blueskyUrl}?${emailUtmParams}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <img src={blueSkyImage} width="20" alt="Bluesky" />
                  </a>
                </span>
                <span>&nbsp; </span>
                <span>
                  <a
                    href={`${instagramUrl}?${emailUtmParams}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <img src={instagramImage} width="20" alt="Instagram" />
                  </a>
                </span>
                <span>&nbsp; </span>
                <span>
                  <a
                    href={`${threadsUrl}?${emailUtmParams}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <img src={threadsImage} width="20" alt="Threads" />
                  </a>
                </span>
                <span>&nbsp; </span>
                <span style={{ color: "black" }}>
                  <a
                    href={`${facebookUrl}?${emailUtmParams}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <img src={facebookImage} width="20" alt="Facebook" />
                  </a>
                </span>
                <span style={{ fontSize: "12pt", lineHeight: "200%" }}>
                  <u />
                  <u />
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
}
